import React, { useEffect, useState } from "react";
import { Button } from "../../ui/button";
import Loader from "../../loader/loader";
import { toast } from "../../../components/ui/use-toast";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { useAuth } from "../../../lib/AuthContext";
import XanoAPI from "../../../lib/XanoAPI";
import { Icons } from "../../../utils/icons";

interface DocumentType {
  id: number;
  name?: string;
  metadata?: { name?: string };
  created_at: string;
  url: string;
}

interface ClientDocumentResponse {
  curPage: number;
  itemsReceived: number | null;
  itemsTotal: number | null;
  nextPage?: number | null;
  prevPage?: number | null;
  offset: number;
  items: DocumentType[];
  pageTotal: number;
  perPage: number;
}

export default function Reports() {
  const { currentUser } = useAuth();
  const [clientDocumentsResponse, setClientDocumentsResponse] =
    useState<ClientDocumentResponse>({
      curPage: 1,
      itemsReceived: 0,
      itemsTotal: 0,
      nextPage: null,
      prevPage: null,
      offset: 0,
      pageTotal: 1,
      perPage: 10,
      items: [],
    });
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = async (page = 1) => {
    try {
      setIsLoading(true);
      setError(null);

      const api = XanoAPI.getInstance();

      // Determine user role and appropriate ID
      const userRole = currentUser?.user_permissions?.[0]?.role || "unknown";
      let documentId: number | string | undefined;
      let idType: string;

      if (userRole === "admin") {
        // Admin users: use agency ID
        documentId =
          currentUser?.agency_id || currentUser?.user_agency?.[0]?.id;
        idType = "agency ID";
      } else if (userRole === "agent") {
        // Agent users: use user ID
        documentId = currentUser?.id;
        idType = "user ID";
      } else {
        // Unknown role: try agency ID first, then user ID
        documentId =
          currentUser?.agency_id ||
          currentUser?.user_agency?.[0]?.id ||
          currentUser?.id;
        idType = "agency/user ID";
      }

      if (!documentId) {
        const errorMsg = `No ${idType} found for the current user.`;
        console.error("❌", errorMsg);
        setError(errorMsg);
        return;
      }

      console.log(`✅ Fetching Client Documents for page ${page}...`);
      console.log(`✅ User role: ${userRole}`);
      console.log(`✅ Using ${idType}: ${documentId}`);
      console.log(`✅ Current user object:`, currentUser);
      console.log(`✅ Agency ID from agency_id:`, currentUser?.agency_id);
      console.log(
        `✅ Agency ID from user_agency:`,
        currentUser?.user_agency?.[0]?.id
      );
      console.log(`✅ User ID:`, currentUser?.id);
      const data: any = await api.getClientDocuments(documentId, userRole);

      console.log("📌 Client Documents API Response:", data);

      // Handle different response formats
      let documents: DocumentType[] = [];

      if (Array.isArray(data)) {
        // If data is directly an array
        documents = data;
      } else if (data && typeof data === "object") {
        if (Array.isArray(data.items)) {
          // Standard paginated response
          documents = data.items;
        } else if (Array.isArray(data.documents)) {
          // Alternative response format
          documents = data.documents;
        } else {
          // Single document object
          documents = [data];
        }
      }

      console.log("📌 Processed documents:", documents);

      setClientDocumentsResponse({
        curPage: page,
        itemsReceived: documents.length,
        itemsTotal: documents.length,
        nextPage: null,
        prevPage: null,
        offset: 0,
        pageTotal: Math.max(1, Math.ceil(documents.length / 10)),
        perPage: 10,
        items: documents,
      });
    } catch (error) {
      const errorMsg = "Failed to fetch client documents.";
      console.error("❌", errorMsg, error);
      setError(errorMsg);
      toast({
        title: "Error",
        description: "Failed to fetch client documents. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments(currentPage);
  }, [currentPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > clientDocumentsResponse.pageTotal) return;
    setCurrentPage(newPage);
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="py-6">
        <h2 className="text-xl font-semibold text-accent mb-4">
          Client Documents
        </h2>
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => fetchDocuments(currentPage)}>Try Again</Button>
        </div>
      </div>
    );
  }

  if (clientDocumentsResponse.items.length === 0) {
    return (
      <div className="py-6">
        <h2 className="text-xl font-semibold text-accent mb-4">
          Client Documents
        </h2>
        <div className="text-center py-8">
          <p className="text-gray-500">No client documents found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <h2 className="text-xl font-semibold text-accent mb-4">
        Client Documents
      </h2>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Document Name</TableHead>
            <TableHead>Date Created</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {clientDocumentsResponse.items.map((document) => (
            <TableRow key={document.id}>
              <TableCell>
                {document.name || document.metadata?.name || "Unnamed Document"}
              </TableCell>
              <TableCell>
                {new Date(document.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <Button
                  variant="outline"
                  onClick={() => window.open(document.url, "_blank")}
                >
                  <Icons.download />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* ✅ Pagination Always Shows (Even on One Page) */}
      <div className="flex items-center justify-between mt-4">
        <Button
          variant="outline"
          disabled={currentPage <= 1}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          <Icons.arrowLeft className="h-4 w-4" />
        </Button>
        <span className="text-xs text-gray-500">
          Page {currentPage} of {clientDocumentsResponse.pageTotal}
        </span>
        <Button
          variant="outline"
          disabled={currentPage >= clientDocumentsResponse.pageTotal}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          <Icons.arrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
